import React from 'react';
import {
  Box, Grid, Card, Typography, Button, Chip,
  Fab, useMediaQuery, BottomNavigation, BottomNavigationAction, Container,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import { registerCharts, areaOptions, barOptions, areaDataset, barDataset } from '../components/ChartTheme';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import HomeIcon from '@mui/icons-material/Home';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoIcon from '@mui/icons-material/Info';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AppsIcon from '@mui/icons-material/Apps';
import RoofingIcon from '@mui/icons-material/Roofing';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AppLayout from '../components/AppLayout';
import { useTenant } from '../context/TenantContext';

registerCharts();

const TEAL  = '#00F1A7';
const TEAL_D = '#00C48C';
const DARK  = '#05080F';

const SEMANA = [
  { d: 'Seg', v: 24 }, { d: 'Ter', v: 32 }, { d: 'Qua', v: 28 },
  { d: 'Qui', v: 41 }, { d: 'Sex', v: 36 }, { d: 'Sáb', v: 18 }, { d: 'Dom', v: 12 },
];

const ATIVIDADE_MODULOS = [
  { mod: 'Consolidação', v: 34 }, { mod: 'Start', v: 52 }, { mod: 'Secretaria', v: 28 },
  { mod: 'Cursos', v: 40 }, { mod: 'Discipulado', v: 22 }, { mod: 'Jornada', v: 30 },
];

function AreaCard({ title, subtitle, icon, onClick, actionLabel }) {
  return (
    <Card
      elevation={0}
      role="region"
      aria-label={title}
      onClick={onClick}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        p: { xs: 3, md: 4 },
        cursor: 'pointer',
        height: '100%',
        minHeight: 220,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 0.18s, box-shadow 0.18s, border-color 0.18s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 14px 34px rgba(0,0,0,0.4)',
          borderColor: 'rgba(0,241,167,0.28)',
        },
      }}
    >
      <Box sx={{
        width: 52, height: 52, borderRadius: 1,
        bgcolor: 'rgba(0,241,167,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 2,
      }}>
        <Box sx={{ color: TEAL, display: 'flex', fontSize: 28 }}>{icon}</Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 800, mb: 0.5, fontSize: '1.35rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          aria-label={`${actionLabel} ${title}`}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={onClick}
          sx={{
            bgcolor: TEAL,
            color: '#0B1200',
            fontWeight: 700,
            borderRadius: 1,
            px: 2.5,
            '&:hover': { bgcolor: TEAL_D },
          }}
        >
          {actionLabel}
        </Button>
      </Box>
    </Card>
  );
}

function parseJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('')));
  } catch { return null; }
}

export default function HomeLanding({ user: userProp, onLogout }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  const user = userProp || parseJwt(localStorage.getItem('token'));
  const role = user?.tipo || 'guest';
  const nome = user?.nome || user?.email?.split('@')[0] || 'usuário';

  const canSeeAll   = role === 'admin' || role === 'super_admin' || role === 'admin_tenant';
  const isPlanADM   = role === 'admin';
  const isConsolidador = role === 'consolidador';
  const isProfessor = role === 'professor' || role === 'monitor';
  const isAluno     = role === 'aluno';
  const isSecretaria = role === 'secretaria' || role === 'pastor';

  const handleGo = (path) => () => navigate(path);

  const cards = [];
  if (canSeeAll || isConsolidador) {
    const path = canSeeAll ? '/gestao-visitantes' : '/area-consolidador';
    cards.push({
      key: 'consolidacao',
      title: 'Consolidação',
      subtitle: 'Cadastro, distribuição e acompanhamento de visitantes. Migração automática para alunos do Start com notificações em tempo real.',
      action: path,
      icon: <PersonAddIcon fontSize="inherit" />,
    });
  }
  if (canSeeAll || isProfessor) {
    cards.push({
      key: 'startfy',
      title: 'Start',
      subtitle: 'Gestão escolar completa: alunos, professores, turmas, períodos letivos, frequência por aula, matrículas, formandos e dashboard com relatórios.',
      action: '/area-startfy',
      icon: <SchoolIcon fontSize="inherit" />,
    });
  }
  if ((canSeeAll || isSecretaria) && !isPlanADM) {
    cards.push({
      key: 'secretaria',
      title: 'Secretaria',
      subtitle: 'Cadastro completo de membros, migração automática de alunos, importação via Excel e histórico detalhado de todas as movimentações.',
      action: '/secretaria',
      icon: <AccountBoxIcon fontSize="inherit" />,
    });
  }
  if (canSeeAll || isSecretaria) {
    cards.push({
      key: 'voluntariado',
      title: 'Voluntariado',
      subtitle: 'Organização por departamentos, criação de eventos e agendas, escala de voluntários com confirmação e relatórios completos por evento.',
      action: '/voluntariado',
      icon: <GroupsIcon fontSize="inherit" />,
    });
  }
  if (canSeeAll || isSecretaria) {
    cards.push({
      key: 'cursos',
      title: 'Cursos e Treinamentos',
      subtitle: 'SaaS de cursos com ministérios, sessões, material multimídia, inscrição de membros e externos, presença por sessão e certificado PDF ao concluir.',
      action: '/cursos',
      icon: <SchoolIcon fontSize="inherit" />,
    });
  }
  if (canSeeAll || isSecretaria) {
    cards.push({
      key: 'discipulado',
      title: 'Discipulado',
      subtitle: 'Relação discipulador/discipulando com status (ativo, pausado, concluído), alerta de membros sem discipulador e vinculação com cursos.',
      action: '/discipulados',
      icon: <GroupIcon fontSize="inherit" />,
    });
  }
  if (canSeeAll || isSecretaria) {
    cards.push({
      key: 'grupos_crescimento',
      title: 'Grupos de Crescimento',
      subtitle: 'Cadastro com líder e co-líder, aprovação pelo pastor, dashboard com indicadores, alerta de membros sem GC e relatório PDF geral.',
      action: '/grupos-crescimento',
      icon: <GroupsIcon fontSize="inherit" />,
    });
  }
  if (canSeeAll || isSecretaria) {
    cards.push({
      key: 'jornada',
      title: 'Jornada do Membro',
      subtitle: 'Timeline horizontal completa: origem, primeira visita, decisão, batismo, discipulador, GC, cursos, função atual, filhos e alertas inteligentes.',
      action: '/jornada/alertas',
      icon: <TimelineIcon fontSize="inherit" />,
    });
  }
  if ((canSeeAll || isSecretaria) && !isPlanADM) {
    cards.push({
      key: 'financeiro',
      title: 'Financeiro',
      subtitle: 'Fluxo de caixa completo: entradas e saídas, classificador de receitas e despesas, balancete mensal, dashboard com indicadores e relatórios.',
      action: '/financeiro',
      icon: <AccountBalanceIcon fontSize="inherit" />,
    });
  }
  if (isAluno) {
    cards.push({
      key: 'aluno',
      title: 'Minha Área',
      subtitle: 'Acompanhe suas aulas, frequências e documentos do curso.',
      action: '/area-aluno',
      icon: <AccountCircleIcon fontSize="inherit" />,
    });
  }
  if (cards.length === 0) {
    cards.push({
      key: 'public',
      title: 'Bem-vindo ao ConectaChurch',
      subtitle: 'Faça login para acessar as áreas do sistema.',
      action: '/login',
      icon: <HomeIcon fontSize="inherit" />,
    });
  }

  const greetHour = new Date().getHours();
  const greet = greetHour < 12 ? 'Bom dia' : greetHour < 18 ? 'Boa tarde' : 'Boa noite';
  const tenant = useTenant();

  const kpis = [
    { icon: <AppsIcon />, value: cards.length, label: 'Áreas do sistema' },
    { icon: <RoofingIcon />, value: '1', label: 'Plano ativo' },
    { icon: <TrendingUpIcon />, value: '7', label: 'Dias de trial' },
    { icon: <SupportAgentIcon />, value: '100%', label: 'Online' },
  ];

  return (
    <AppLayout>
      <Box sx={{ py: { xs: 2, md: 3 } }}>

        {/* Saudação */}
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <Typography variant="body2" sx={{ color: TEAL, fontWeight: 700, letterSpacing: 1, mb: 0.5, textTransform: 'uppercase', fontSize: 12 }}>
            {greet}{nome && `, ${nome}`}
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2 }}>
            {tenant ? `Bem-vindo ao Espaço Digital da ${tenant.nome}` : <>O que você vai<br />fazer hoje?</>}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InfoIcon />}
            onClick={() => navigate('/recursos')}
            sx={{ mt: 1.5, borderColor: 'rgba(0,241,167,0.4)', color: TEAL, fontWeight: 600, '&:hover': { borderColor: TEAL, bgcolor: 'rgba(0,241,167,0.08)' } }}
          >
            Ver todos os recursos do sistema
          </Button>
        </Box>

        {/* KPIs */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {kpis.map((k, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 46, height: 46, borderRadius: 1, bgcolor: 'rgba(0,241,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEAL, flexShrink: 0 }}>
                  {k.icon}
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '4.4rem', color: 'text.primary', lineHeight: 1 }}>{k.value}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{k.label}</Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Gráficos */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={7}>
            <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3 }}>
              <Typography variant="h3" sx={{ mb: 2, fontSize: '1rem' }}>Atividade da semana</Typography>
              <Box sx={{ height: 220 }}>
                <Line
                  data={{ labels: SEMANA.map(s => s.d), datasets: [{ ...areaDataset(SEMANA.map(s => s.v)), label: 'Atividade' }] }}
                  options={areaOptions()}
                />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3, height: '100%' }}>
              <Typography variant="h3" sx={{ mb: 2, fontSize: '1rem' }}>Engajamento por módulo</Typography>
              <Box sx={{ height: 220 }}>
                <Bar
                  data={{ labels: ATIVIDADE_MODULOS.map(m => m.mod), datasets: [{ ...barDataset(ATIVIDADE_MODULOS.map(m => m.v)) }] }}
                  options={barOptions()}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Jornada do usuário */}
        <Box sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: { xs: 3, md: 4 },
          mb: canSeeAll ? 3 : 0,
        }}>
          <Typography variant="h3" sx={{ mb: 2.5, fontSize: '1rem' }}>
            Jornada do usuário
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
            {[
              { label: '1. Consolidação', bg: '#0A141C',  color: '#E9F1EF' },
              { label: '→', arrow: true },
              { label: '2. Discipulado',  bg: '#0A141C',  color: '#E9F1EF' },
              { label: '→', arrow: true },
              { label: '3. Start',        bg: TEAL,  color: '#0B1200' },
              { label: '→', arrow: true },
              { label: '4. GCs',          bg: '#0A141C',  color: '#E9F1EF' },
              { label: '→', arrow: true },
              { label: '5. Secretaria',   bg: '#0A141C',  color: '#E9F1EF' },
            ].map((item, i) => item.arrow ? (
              <Typography key={i} sx={{ color: '#93A7A3', fontWeight: 700, fontSize: 18 }}>→</Typography>
            ) : (
              <Chip
                key={i}
                label={item.label}
                sx={{ bgcolor: item.bg, color: item.color, fontWeight: 700, fontSize: 13, px: 0.5, height: 32 }}
              />
            ))}
          </Box>
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', maxWidth: 560 }}>
            Cada etapa tem responsáveis e checkpoints para garantir qualidade e rastreabilidade desde a recepção até o cadastro oficial.
          </Typography>
        </Box>

        {/* Atalhos administrativos */}
        {canSeeAll && (
          <Box sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: { xs: 3, md: 4 },
          }}>
            <Typography variant="h3" sx={{ mb: 2.5, fontSize: '1rem' }}>
              Atalhos administrativos
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                { label: 'Painel Start', path: '/area-startfy' },
                { label: 'Dashboard', path: '/dashboard' },
                { label: 'Usuários', path: '/usuarios' },
                { label: 'Relatórios', path: '/relatorios' },
              ].map(a => (
                <Button
                  key={a.path}
                  variant="outlined"
                  onClick={() => navigate(a.path)}
                  sx={{ borderColor: 'rgba(0,241,167,0.4)', color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: 'rgba(0,241,167,0.08)' } }}
                >
                  {a.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}

      </Box>
    </AppLayout>
  );
}
